import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/TranslationContext';
import { Navbar, Footer } from '../components/Layout';
import DeviceManager from '../components/DeviceManager';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../components/ui/dialog';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '../components/ui/collapsible';
import { PhoneInput } from '../components/PhoneInput';
import {
  User, Upload, Crown, CreditCard, Smartphone, AlertTriangle, ChevronDown,
  Download, Trash2, ShieldCheck, KeyRound, Globe, Award, Mail, Receipt
} from 'lucide-react';
import { toast } from 'sonner';
import { getApiBaseUrl } from '@/lib/apiBase';

const API_URL = getApiBaseUrl();
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const PLAN_LABELS = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  yearly: 'Yearly',
  lifetime: 'Premium Access',
};

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, token, logout, refreshUser } = useAuth();
  const { t, language, setLanguage, languages } = useTranslation();
  const headers = { Authorization: `Bearer ${token}` };

  // Section 1: Profile Information
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [phoneCountryCode, setPhoneCountryCode] = useState('+91');
  const [bio, setBio] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [uploadingCertPhoto, setUploadingCertPhoto] = useState(false);

  // Section 2: Account Settings
  const [directoryOptIn, setDirectoryOptIn] = useState(true);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  // GST billing details (used on invoices for B2B customers)
  const [gstin, setGstin] = useState('');
  const [gstState, setGstState] = useState('');
  const [address, setAddress] = useState('');
  const [indianStates, setIndianStates] = useState([]);

  // Section 3: Subscription & Billing
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [resendingInvoice, setResendingInvoice] = useState(null);
  const [downloadingInvoice, setDownloadingInvoice] = useState(null);

  // Section 5: Danger Zone
  const [dangerOpen, setDangerOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!user) return;
    setName(user.name || '');
    setPhone(user.phone || '');
    setPhoneCountryCode(user.phone_country_code || '+91');
    setBio(user.bio || '');
    setDirectoryOptIn(user.directory_opt_in !== false);
    setGstin(user.gstin || '');
    setGstState(user.state || '');
    setAddress(user.address || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    axios.get(`${API_URL}/gst/states`).then((res) => setIndianStates(res.data || [])).catch(() => {});
  }, []);

  const fetchInvoices = async () => {
    setInvoicesLoading(true);
    try {
      const res = await axios.get(`${API_URL}/invoices/my`, { headers });
      setInvoices(res.data || []);
    } catch (err) {
      setInvoices([]);
    } finally {
      setInvoicesLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const [statusRes, paymentsRes] = await Promise.allSettled([
          axios.get(`${API_URL}/payments/subscription/status`, { headers }),
          axios.get(`${API_URL}/users/me/payments`, { headers }),
        ]);
        if (statusRes.status === 'fulfilled') setSubscriptionStatus(statusRes.value.data);
        if (paymentsRes.status === 'fulfilled') setPayments(paymentsRes.value.data || []);
      } catch (err) {
        // ignore — billing section just shows fallback state
      }
    })();
    fetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const initials = (name || user?.name || '?').trim().slice(0, 1).toUpperCase();

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    setUploadingAvatar(true);
    try {
      await axios.post(`${API_URL}/users/me/avatar`, formData, { headers });
      await refreshUser();
      toast.success(t('profile_toast_photo_updated', 'Avatar updated'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('profile_toast_photo_fail', 'Failed to upload photo'));
    } finally {
      setUploadingAvatar(false);
      e.target.value = '';
    }
  };

  const handleCertPhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    setUploadingCertPhoto(true);
    try {
      await axios.post(`${API_URL}/users/me/certificate-photo`, formData, { headers });
      await refreshUser();
      toast.success(t('profile_toast_cert_photo_updated', 'Certificate photo updated'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('profile_toast_cert_photo_fail', 'Failed to upload photo'));
    } finally {
      setUploadingCertPhoto(false);
      e.target.value = '';
    }
  };

  const handleSaveProfile = async () => {
    if (!name.trim()) {
      toast.error(t('profile_name_required', 'Name is required'));
      return;
    }
    setSavingProfile(true);
    try {
      await axios.patch(`${API_URL}/users/me`, {
        name,
        phone,
        phone_country_code: phoneCountryCode,
        bio,
        gstin,
        state: gstState,
        address,
      }, { headers });
      await refreshUser();
      toast.success(t('profile_toast_saved', 'Profile updated'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('profile_toast_save_fail', 'Failed to update profile'));
    } finally {
      setSavingProfile(false);
    }
  };

  const handleDownloadInvoice = async (invoiceNumber) => {
    setDownloadingInvoice(invoiceNumber);
    try {
      const res = await axios.get(`${API_URL}/invoices/${invoiceNumber}/download`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice-${invoiceNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(t('profile_invoice_download_fail', 'Failed to download invoice'));
    } finally {
      setDownloadingInvoice(null);
    }
  };

  const handleResendInvoice = async (invoiceNumber) => {
    setResendingInvoice(invoiceNumber);
    try {
      await axios.post(`${API_URL}/invoices/${invoiceNumber}/resend`, {}, { headers });
      toast.success(t('profile_invoice_resent', 'Invoice emailed to you'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('profile_invoice_resend_fail', 'Failed to resend invoice'));
    } finally {
      setResendingInvoice(null);
    }
  };

  const handleDirectoryToggle = async (value) => {
    setDirectoryOptIn(value);
    try {
      await axios.patch(`${API_URL}/users/me`, { directory_opt_in: value }, { headers });
      await refreshUser();
      toast.success(value
        ? t('profile_toast_directory_in', 'You now appear in the public Student Directory')
        : t('profile_toast_directory_out', 'You have been removed from the public Student Directory'));
    } catch (err) {
      setDirectoryOptIn(!value);
      toast.error(err?.response?.data?.detail || t('profile_toast_directory_fail', 'Failed to update directory preference'));
    }
  };

  const handleLanguageChange = async (code) => {
    setLanguage(code);
    try {
      await axios.patch(`${API_URL}/users/me`, { language_preference: code }, { headers });
      await refreshUser();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('profile_language_fail', 'Failed to save language preference'));
    }
  };

  const handleChangePassword = async () => {
    if (newPassword.length < 8 || !/\d/.test(newPassword)) {
      toast.error(t('profile_password_requirements', 'Password must be at least 8 characters and include a number'));
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error(t('profile_password_mismatch', 'New passwords do not match'));
      return;
    }
    setChangingPassword(true);
    try {
      await axios.post(`${API_URL}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword,
      }, { headers });
      toast.success(t('profile_password_changed', 'Password changed successfully'));
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('profile_password_fail', 'Failed to change password'));
    } finally {
      setChangingPassword(false);
    }
  };

  const handleExportData = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API_URL}/users/me/export`, { headers });
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const dateStr = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `thachari-my-data-${dateStr}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(t('profile_export_success', 'Your data export has downloaded'));
    } catch (err) {
      toast.error(t('profile_export_fail', 'Failed to export your data'));
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await axios.post(`${API_URL}/auth/delete-account`, {
        password: deletePassword,
        confirmation: deleteConfirmText,
      }, { headers });
      setDeleteDialogOpen(false);
      await logout();
      navigate('/');
      toast.success(t('profile_account_deleted', 'Your account has been deleted'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('profile_delete_fail', 'Failed to delete account'));
    } finally {
      setDeleting(false);
    }
  };

  if (!user) return null;

  const planKey = subscriptionStatus?.plan && subscriptionStatus.plan !== 'none' ? subscriptionStatus.plan : 'free';
  const planLabel = subscriptionStatus?.is_premium
    ? (PLAN_LABELS[planKey] || planKey)
    : t('profile_plan_free', 'Free');

  return (
    <div className="min-h-screen bg-background" data-testid="profile-page">
      <Navbar />
      <main className="pt-20 pb-16">
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-8 space-y-8">
          <div>
            <h1 className="font-serif text-3xl font-medium">{t('profile_page_title', 'My Profile')}</h1>
            <p className="text-muted-foreground mt-1">
              {t('profile_page_subtitle', 'Manage your account, subscription, and privacy settings')}
            </p>
          </div>

          {/* Section 1: Profile Information */}
          <Card data-testid="profile-info-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                {t('profile_section_info', 'Profile Information')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center gap-4">
                <Avatar className="w-20 h-20">
                  <AvatarImage src={user?.photo_url ? `${BACKEND_URL}${user.photo_url}` : undefined} alt={user?.name} />
                  <AvatarFallback className="text-2xl">{initials}</AvatarFallback>
                </Avatar>
                <div>
                  <Label htmlFor="avatar-input" className="cursor-pointer inline-flex items-center gap-2 text-sm font-medium text-accent hover:underline">
                    <Upload className="w-4 h-4" />
                    {uploadingAvatar
                      ? t('profile_uploading_photo', 'Uploading...')
                      : t('profile_change_photo', 'Change photo')}
                  </Label>
                  <input
                    id="avatar-input"
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={handleAvatarUpload}
                    disabled={uploadingAvatar}
                    data-testid="avatar-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('profile_photo_hint', 'JPEG, PNG or WebP, up to 5MB. Will be cropped to a square.')}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="profile-name">{t('profile_name_label', 'Full Name')}</Label>
                  <Input
                    id="profile-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    data-testid="profile-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="profile-email">{t('profile_email_label', 'Email')}</Label>
                  <Input
                    id="profile-email"
                    value={user?.email || ''}
                    readOnly
                    disabled
                    className="text-muted-foreground bg-muted"
                    data-testid="profile-email-input"
                  />
                </div>
              </div>

              <div>
                <Label>{t('profile_phone_label', 'Phone Number')}</Label>
                <PhoneInput
                  countryCode={phoneCountryCode}
                  phone={phone}
                  onCountryCodeChange={setPhoneCountryCode}
                  onPhoneChange={setPhone}
                  testIdPrefix="profile-phone"
                />
              </div>

              <div>
                <Label htmlFor="profile-bio">{t('profile_bio_label', 'Short Bio')}</Label>
                <Textarea
                  id="profile-bio"
                  value={bio}
                  onChange={(e) => setBio(e.target.value.slice(0, 300))}
                  placeholder={t('profile_bio_placeholder', 'Tell other students a bit about yourself...')}
                  maxLength={300}
                  rows={4}
                  data-testid="profile-bio-input"
                />
                <p className="text-xs text-muted-foreground text-right mt-1">{bio.length}/300</p>
              </div>

              <div className="border-t pt-6">
                <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <Receipt className="w-4 h-4" />
                  {t('profile_billing_details_title', 'Billing Details (for GST invoices)')}
                </h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="profile-gstin">{t('profile_gstin_label', 'GSTIN (optional, for B2B invoices)')}</Label>
                    <Input
                      id="profile-gstin"
                      value={gstin}
                      onChange={(e) => setGstin(e.target.value.toUpperCase())}
                      placeholder="22AAAAA0000A1Z5"
                      maxLength={15}
                      data-testid="profile-gstin-input"
                    />
                  </div>
                  <div>
                    <Label>{t('profile_state_label', 'State')}</Label>
                    <Select value={gstState} onValueChange={setGstState}>
                      <SelectTrigger data-testid="profile-state-select"><SelectValue placeholder={t('profile_state_placeholder', 'Select your state')} /></SelectTrigger>
                      <SelectContent>
                        {indianStates.map((s) => (
                          <SelectItem key={s.code} value={s.name}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="mt-4">
                  <Label htmlFor="profile-address">{t('profile_address_label', 'Address')}</Label>
                  <Textarea
                    id="profile-address"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder={t('profile_address_placeholder', 'Street, City, Pincode')}
                    rows={2}
                    data-testid="profile-address-input"
                  />
                </div>
              </div>

              <div className="border-t pt-6">
                <div className="flex items-start gap-4">
                  <Avatar className="w-16 h-16 rounded-md">
                    <AvatarImage
                      src={user?.certificate_photo_url ? `${BACKEND_URL}${user.certificate_photo_url}` : undefined}
                      alt="Certificate"
                      className="rounded-md"
                    />
                    <AvatarFallback className="rounded-md text-xl">
                      <Award className="w-6 h-6" />
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="text-sm font-medium">{t('profile_cert_photo_title', 'Certificate Photo')}</p>
                    <p className="text-xs text-muted-foreground mb-2">
                      {t('profile_cert_photo_hint', 'This photo appears on your certificates')}
                    </p>
                    <Label htmlFor="cert-photo-input" className="cursor-pointer inline-flex items-center gap-2 text-sm font-medium text-accent hover:underline">
                      <Upload className="w-4 h-4" />
                      {uploadingCertPhoto
                        ? t('profile_uploading_photo', 'Uploading...')
                        : t('profile_change_photo', 'Change photo')}
                    </Label>
                    <input
                      id="cert-photo-input"
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      onChange={handleCertPhotoUpload}
                      disabled={uploadingCertPhoto}
                      data-testid="cert-photo-input"
                    />
                  </div>
                </div>
              </div>

              <div className="sticky bottom-4 z-10 flex justify-end pt-2">
                <Button
                  onClick={handleSaveProfile}
                  disabled={savingProfile}
                  className="shadow-lg"
                  data-testid="save-profile-btn"
                >
                  {savingProfile ? t('profile_saving', 'Saving...') : t('profile_save', 'Save Changes')}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Section 2: Account Settings */}
          <Card data-testid="account-settings-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <KeyRound className="w-5 h-5" />
                {t('profile_section_account', 'Account Settings')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <h3 className="text-sm font-semibold">{t('profile_change_password_title', 'Change Password')}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Input
                    type="password"
                    placeholder={t('profile_current_password', 'Current password')}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    data-testid="current-password-input"
                  />
                  <Input
                    type="password"
                    placeholder={t('profile_new_password', 'New password')}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    data-testid="new-password-input"
                  />
                  <Input
                    type="password"
                    placeholder={t('profile_confirm_password', 'Confirm new password')}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    data-testid="confirm-password-input"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  {t('profile_password_hint', 'Minimum 8 characters, at least one number')}
                </p>
                <Button
                  variant="outline"
                  onClick={handleChangePassword}
                  disabled={changingPassword || !currentPassword || !newPassword || !confirmPassword}
                  data-testid="change-password-btn"
                >
                  {changingPassword ? t('profile_saving', 'Saving...') : t('profile_change_password_btn', 'Change Password')}
                </Button>
              </div>

              <div className="border-t pt-6 flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">{t('profile_directory_title', 'Student Directory')}</p>
                    <p className="text-xs text-muted-foreground">
                      {t('profile_directory_desc', 'Show my profile in the Student Directory')}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={directoryOptIn}
                  onCheckedChange={handleDirectoryToggle}
                  data-testid="directory-opt-in-toggle"
                />
              </div>

              <div className="border-t pt-6 flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">{t('profile_language_title', 'Language')}</p>
                    <p className="text-xs text-muted-foreground">
                      {t('profile_language_desc', 'Preferred language for the platform')}
                    </p>
                  </div>
                </div>
                <Select value={language} onValueChange={handleLanguageChange}>
                  <SelectTrigger className="w-48" data-testid="language-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(languages.length ? languages : [{ code: 'en', name: 'English' }]).map((lang) => (
                      <SelectItem key={lang.code} value={lang.code}>
                        {lang.flag ? `${lang.flag} ` : ''}{lang.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Section 3: Subscription & Billing */}
          <Card data-testid="billing-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                {t('profile_section_billing', 'Subscription & Billing')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <Badge
                    className={subscriptionStatus?.is_premium ? 'bg-accent text-accent-foreground' : ''}
                    variant={subscriptionStatus?.is_premium ? 'default' : 'secondary'}
                    data-testid="plan-badge"
                  >
                    {subscriptionStatus?.is_lifetime && <Crown className="w-3 h-3 mr-1" />}
                    {planLabel}
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    {subscriptionStatus?.is_lifetime
                      ? t('profile_never_expires', 'Never expires')
                      : subscriptionStatus?.expiry_date
                        ? `${t('profile_expires_on', 'Expires on')} ${new Date(subscriptionStatus.expiry_date).toLocaleDateString()}`
                        : subscriptionStatus?.is_premium
                          ? ''
                          : t('profile_no_active_plan', 'No active subscription')}
                  </span>
                </div>
                {subscriptionStatus?.is_lifetime ? (
                  <Badge variant="outline" className="border-green-500 text-green-600 gap-1">
                    <ShieldCheck className="w-3 h-3" />
                    {t('profile_lifetime_active', 'Premium Access — Active')}
                  </Badge>
                ) : (
                  <Button onClick={() => navigate('/pricing')} data-testid="upgrade-plan-btn">
                    {t('profile_upgrade_plan', 'Upgrade Plan')}
                  </Button>
                )}
              </div>

              <div>
                <h3 className="text-sm font-semibold mb-3">{t('profile_payment_history', 'Payment History')}</h3>
                {payments.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {t('profile_no_payments', 'No payments yet')}
                  </p>
                ) : (
                  <div className="overflow-x-auto -mx-2">
                    <table className="w-full text-sm" data-testid="payments-table">
                      <thead>
                        <tr className="text-left text-muted-foreground border-b">
                          <th className="py-2 px-2 font-medium">{t('profile_pay_date', 'Date')}</th>
                          <th className="py-2 px-2 font-medium">{t('profile_pay_plan', 'Plan')}</th>
                          <th className="py-2 px-2 font-medium">{t('profile_pay_amount', 'Amount')}</th>
                          <th className="py-2 px-2 font-medium">{t('profile_pay_status', 'Status')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payments.slice(0, 5).map((p, idx) => (
                          <tr key={idx} className="border-b last:border-0">
                            <td className="py-2 px-2">{p.date ? new Date(p.date).toLocaleDateString() : '—'}</td>
                            <td className="py-2 px-2 capitalize">{p.plan || '—'}</td>
                            <td className="py-2 px-2">₹{Number(p.amount || 0).toFixed(2)}</td>
                            <td className="py-2 px-2">
                              <Badge variant={p.status === 'paid' ? 'default' : 'secondary'} className="capitalize">
                                {p.status || 'pending'}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div>
                <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <Receipt className="w-4 h-4" />
                  {t('profile_invoices_title', 'Invoices')}
                </h3>
                {invoicesLoading ? (
                  <p className="text-sm text-muted-foreground">{t('profile_invoices_loading', 'Loading invoices...')}</p>
                ) : invoices.length === 0 ? (
                  <p className="text-sm text-muted-foreground" data-testid="invoices-empty-state">
                    {t('profile_no_invoices', 'No invoices yet — purchase a plan to get started')}
                  </p>
                ) : (
                  <div className="overflow-x-auto -mx-2">
                    <table className="w-full text-sm" data-testid="invoices-table">
                      <thead>
                        <tr className="text-left text-muted-foreground border-b">
                          <th className="py-2 px-2 font-medium">{t('profile_inv_number', 'Invoice No')}</th>
                          <th className="py-2 px-2 font-medium">{t('profile_inv_date', 'Date')}</th>
                          <th className="py-2 px-2 font-medium">{t('profile_inv_plan', 'Plan')}</th>
                          <th className="py-2 px-2 font-medium">{t('profile_inv_amount', 'Amount')}</th>
                          <th className="py-2 px-2 font-medium">{t('profile_inv_gst', 'GST')}</th>
                          <th className="py-2 px-2 font-medium">{t('profile_inv_total', 'Total')}</th>
                          <th className="py-2 px-2 font-medium text-right">{t('profile_inv_actions', 'Actions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoices.map((inv) => {
                          const gst = (inv.cgst_amount || 0) + (inv.sgst_amount || 0) + (inv.igst_amount || 0);
                          return (
                            <tr key={inv.invoice_number} className="border-b last:border-0">
                              <td className="py-2 px-2 font-medium">{inv.invoice_number}</td>
                              <td className="py-2 px-2">{inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString() : '—'}</td>
                              <td className="py-2 px-2 capitalize">{inv.plan_type}</td>
                              <td className="py-2 px-2">₹{Number(inv.base_amount || 0).toFixed(2)}</td>
                              <td className="py-2 px-2">₹{gst.toFixed(2)}</td>
                              <td className="py-2 px-2 font-medium">₹{Number(inv.total_amount || 0).toFixed(2)}</td>
                              <td className="py-2 px-2">
                                <div className="flex items-center justify-end gap-1">
                                  <Button
                                    size="icon" variant="ghost"
                                    onClick={() => handleDownloadInvoice(inv.invoice_number)}
                                    disabled={downloadingInvoice === inv.invoice_number}
                                    title={t('profile_inv_download', 'Download')}
                                  >
                                    <Download className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    size="icon" variant="ghost"
                                    onClick={() => handleResendInvoice(inv.invoice_number)}
                                    disabled={resendingInvoice === inv.invoice_number}
                                    title={t('profile_inv_resend', 'Resend to my email')}
                                  >
                                    <Mail className="w-4 h-4" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Section 4: Devices */}
          <Card data-testid="devices-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Smartphone className="w-5 h-5" />
                {t('profile_section_devices', 'Devices')}
              </CardTitle>
              <CardDescription>
                {t('profile_devices_desc', 'Manage your logged-in devices')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <DeviceManager />
            </CardContent>
          </Card>

          {/* Section 5: Danger Zone */}
          <Collapsible open={dangerOpen} onOpenChange={setDangerOpen}>
            <Card className="border-destructive/40" data-testid="danger-zone-card">
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="w-full flex items-center justify-between p-6 text-left"
                  data-testid="danger-zone-toggle"
                >
                  <span className="flex items-center gap-2 font-semibold text-destructive">
                    <AlertTriangle className="w-5 h-5" />
                    {t('profile_danger_zone', 'Danger Zone')}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-destructive transition-transform ${dangerOpen ? 'rotate-180' : ''}`} />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="space-y-4 border-t pt-6">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                      <p className="text-sm font-medium">{t('profile_export_title', 'Export My Data')}</p>
                      <p className="text-xs text-muted-foreground">
                        {t('profile_export_desc', 'Download a JSON file of your profile, progress, certificates, and payment history')}
                      </p>
                    </div>
                    <Button variant="outline" onClick={handleExportData} disabled={exporting} data-testid="export-data-btn">
                      <Download className="w-4 h-4 mr-2" />
                      {exporting ? t('profile_exporting', 'Exporting...') : t('profile_export_btn', 'Export My Data')}
                    </Button>
                  </div>

                  <div className="flex items-center justify-between flex-wrap gap-4 border-t pt-4">
                    <div>
                      <p className="text-sm font-medium text-destructive">{t('profile_delete_title', 'Delete My Account')}</p>
                      <p className="text-xs text-muted-foreground">
                        {t('profile_delete_desc', 'Permanently delete your account, progress, and certificates')}
                      </p>
                    </div>
                    <Button
                      variant="destructive"
                      onClick={() => setDeleteDialogOpen(true)}
                      data-testid="delete-account-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      {t('profile_delete_btn', 'Delete My Account')}
                    </Button>
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        </div>
      </main>
      <Footer />

      {/* Delete Account Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={(open) => {
        setDeleteDialogOpen(open);
        if (!open) {
          setDeleteConfirmText('');
          setDeletePassword('');
        }
      }}>
        <DialogContent data-testid="delete-account-dialog">
          <DialogHeader>
            <DialogTitle className="text-destructive flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              {t('profile_delete_confirm_title', 'Delete Your Account')}
            </DialogTitle>
            <DialogDescription>
              {t('profile_delete_confirm_desc', 'This will permanently delete your account, progress, and certificates. Type DELETE to confirm.')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="delete-password">{t('profile_delete_password_label', 'Your password')}</Label>
              <Input
                id="delete-password"
                type="password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                data-testid="delete-password-input"
              />
            </div>
            <div>
              <Label htmlFor="delete-confirm">{t('profile_delete_type_label', 'Type DELETE to confirm')}</Label>
              <Input
                id="delete-confirm"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="DELETE"
                data-testid="delete-confirm-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              {t('profile_cancel', 'Cancel')}
            </Button>
            <Button
              variant="destructive"
              disabled={deleteConfirmText !== 'DELETE' || !deletePassword || deleting}
              onClick={handleDeleteAccount}
              data-testid="confirm-delete-btn"
            >
              {deleting ? t('profile_deleting', 'Deleting...') : t('profile_delete_confirm_btn', 'Permanently Delete My Account')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProfilePage;
